import { type components, TableTypeEnum } from "@selector/shared/openapi";
import { useEffect, useMemo, useState } from "react";

import { useGenomeViewerStore } from "../../../store/store.ts";
import { getTrackTypeFromTableType } from "../../../utils/type-conversion.ts";
import { TableRestriction } from "../settings.ts";
import type { TableTypeItem } from "../TableTypeSelect.tsx";

type RegionRequest = components["schemas"]["RegionRequest"];

export function useTableRestriction(
  tableType: TableTypeItem,
  visibleTrackGroups: { organismId: number; contigId: number; groupId: string }[],
  isPangemome: boolean,
) {
  const [tableRestriction, setTableRestriction] = useState<TableRestriction>(
    isPangemome ? TableRestriction.REGION : TableRestriction.GENOME,
  );
  const [visibleRegions, setVisibleRegions] = useState<RegionRequest[]>([]);
  const [featureIds, setFeatureIds] = useState<number[]>([]);

  const isInteracting = useGenomeViewerStore(
    (state) => state.isScrolling || state.isResizingPanel || state.isDraggingRegionIndicator,
  );
  const alignedFamily = useGenomeViewerStore((state) => state.alignedFamily);
  const isSelecting = useGenomeViewerStore((state) => state.isSelecting);
  const getVisibleRange = useGenomeViewerStore((state) => state.getVisibleRange);
  const getFeaturesGroups = useGenomeViewerStore((state) => state.getFeaturesGroups);

  useEffect(() => {
    if (tableRestriction !== TableRestriction.REGION || isInteracting || tableType.type === TableTypeEnum.ORGANISM) {
      return;
    }
    const newVisibleRegions: RegionRequest[] = visibleTrackGroups.map((group): RegionRequest => {
      const range = getVisibleRange(group.groupId);
      return { contigId: group.contigId, range };
    });
    setVisibleRegions(newVisibleRegions);
  }, [alignedFamily, isInteracting, tableType, tableRestriction, visibleTrackGroups, getVisibleRange]);

  useEffect(() => {
    if (tableRestriction !== TableRestriction.SELECTION || isSelecting || tableType.type === TableTypeEnum.ORGANISM) {
      return;
    }
    setFeatureIds(
      getFeaturesGroups()
        .get(getTrackTypeFromTableType(tableType.type))
        ?.filter((group) => (group.subType ? group.subType?.id === tableType.subType?.id : true))
        ?.flatMap((group) => group.featureIds) ?? [],
    );
  }, [isSelecting, tableRestriction, tableType, getFeaturesGroups]);

  const regions = useMemo(() => {
    switch (tableRestriction) {
      case TableRestriction.GENOME:
        return undefined;
      case TableRestriction.REGION:
        return visibleRegions;
      case TableRestriction.CONTIG:
        return visibleTrackGroups.map(({ contigId }) => ({ contigId }));
      case TableRestriction.SELECTION:
        return undefined;
    }
  }, [tableRestriction, visibleTrackGroups, visibleRegions]);

  return { tableRestriction, featureIds, regions, setTableRestriction };
}
